import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { Form } from 'antd';

import { DASHBOARD_ITEM_API_NAMES } from '@src/constants/commons';
import useSelectGroupsWithPagination from '@src/packages/utils/hooks/collections/useSelectGroupsWithPagination';
import CounterChartService from '@src/services/CounterChartService';

import {
  ALL_TEMPLATES,
  DASHBOARDS_EVENT_TYPE,
  DIVISIONS_TYPE,
  EXPRESSION_VALUE_TYPE,
  EXPRESSION_VALUE_TYPE_DEFAULT,
  LAYOUT_BY_COMPONENT_TYPE,
  USERS_TYPE,
} from '../../../constants';
import { useManageDivisionValue } from '../../../hooks';
import { PAGE_SIZE } from '../constants';

const useCounterForm = ({ chartId, divId, boardId }) => {
  const [type, setType] = useState(null);
  const [selectedSchedules, setSelectedSchedules] = useState({ byId: {}, selectedAll: false });
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchedChartData, setFetchedChartData] = useState(null);
  const [initialScheduleIds, setInitialScheduleIds] = useState([]);

  const orgId = useSelector(state => state.organizations.organization.id);
  const userOrgDivisions = useSelector(({ divisions }) => divisions?.assigned?.data?.divisions);

  const internalFormRef = useRef(null);
  const counterChartService = useMemo(() => new CounterChartService(), []);

  const valueType = Form.useWatch('valueType', internalFormRef?.current ?? {});
  const watchDivisionFilter = Form.useWatch('divisionFilter', internalFormRef?.current ?? {});
  const watchDivisionIds = Form.useWatch('divisionIds', internalFormRef?.current ?? []) || [];
  const watchUsersType = Form.useWatch('usersType', internalFormRef?.current ?? {});

  const divisionIdsToSubmit = useManageDivisionValue({
    divisionFilterValue: watchDivisionFilter,
    divisionIds: watchDivisionIds,
    divId,
  });

  const divisionsOptions = useMemo(() => {
    const divisions = userOrgDivisions || {};
    return Object.values(divisions)
      .map(division => ({
        label: division.name,
        value: division.id,
      }))
      .filter(division => (divId ? division.value === divId : true));
  }, [userOrgDivisions, divId]);

  const {
    data: groupOptions,
    loading: groupsLoading,
    handlePopupScroll: handlePopupScrollGroups,
  } = useSelectGroupsWithPagination({
    orgId,
    pageSize: PAGE_SIZE,
    divIds: divId ? [divId] : null,
    orderByConfig: { field: 'name', direction: 'asc' },
  });

  const handleClear = useCallback(() => {
    internalFormRef.current?.resetFields();

    setType(null);
    setSelectedSchedules({ byId: {}, selectedAll: false });
    internalFormRef.current.setFieldValue('usersType', USERS_TYPE.ALL);
  }, []);

  const resetUsersAndGroups = useCallback(() => {
    internalFormRef.current.setFieldValue('userIds', []);
    internalFormRef.current.setFieldValue('groupIds', []);
  }, []);

  const buildBody = useCallback(() => {
    const title = internalFormRef.current.getFieldValue('title');
    const description = internalFormRef.current.getFieldValue('description');
    const userIds = internalFormRef.current.getFieldValue('userIds');
    const groupIds = internalFormRef.current.getFieldValue('groupIds');
    const formType = internalFormRef.current.getFieldValue('type');
    const formValueType = internalFormRef.current.getFieldValue('valueType');
    const scheduleIds = internalFormRef.current.getFieldValue('schedulesIds');
    const templateIds = internalFormRef.current.getFieldValue('templateIds');
    const percentageConfig = internalFormRef.current.getFieldValue('percentageConfig');
    const layout = internalFormRef.current.getFieldValue('layout');
    const usersType = internalFormRef.current.getFieldValue('usersType');

    const usersOrGroups = {};
    if (usersType === USERS_TYPE.ALL) {
      usersOrGroups.userIds = [];
      usersOrGroups.groupIds = [];
    } else if (usersType === USERS_TYPE.USERS) {
      usersOrGroups.userIds = userIds;
      usersOrGroups.groupIds = [];
    } else if (usersType === USERS_TYPE.GROUPS) {
      usersOrGroups.userIds = [];
      usersOrGroups.groupIds = groupIds;
    }

    const body = {
      title,
      description,
      apiName: DASHBOARD_ITEM_API_NAMES.SCHEDULES_COMPLETION,
      layout: layout || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.COUNTER_CHARTS],
      queryParams: {
        ...usersOrGroups,
        scheduleIds: selectedSchedules.selectedAll ? [] : scheduleIds,
        divisionIds: divisionIdsToSubmit,
        templateIds: templateIds?.includes(ALL_TEMPLATES) ? [] : templateIds,
        type: formType,
        valueType: formValueType,
      },
    };

    if (formValueType === EXPRESSION_VALUE_TYPE.PERCENTAGE && percentageConfig) {
      body.queryParams.percentageConfig = percentageConfig;
    }

    if (internalFormRef.current) {
      internalFormRef.current.setFieldValue('counterFormData', body);
      internalFormRef.current.setFieldValue('id', chartId);
    }
    setType(formType);
  }, [chartId, selectedSchedules, divisionIdsToSubmit]);

  const resetSelectedSchedules = useCallback(() => {
    setSelectedSchedules({ byId: {}, selectedAll: true });
    internalFormRef.current.setFieldValue('schedulesIds', []);
  }, []);

  const resetTemplateIds = useCallback(() => {
    internalFormRef.current.setFieldValue('templateIds', []);
  }, []);

  const getImperativeHandle = useCallback(
    () => ({
      ...internalFormRef.current,
      handleClear,
      submit: () => {
        internalFormRef.current?.submit();
      },
    }),
    [handleClear],
  );

  useEffect(() => {
    internalFormRef.current.setFieldValue(
      'schedulesIds',
      Object.keys(selectedSchedules?.byId || {}),
    );
  }, [selectedSchedules]);

  useEffect(() => {
    if (!chartId || !boardId || !orgId) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await counterChartService.getCounterChart({ orgId, divId, boardId, chartId });

        if (res) {
          setFetchedChartData(res);
          const {
            title: titleFetched,
            description: descriptionFetched,
            queryParams: queryParamsFetched = {},
            layout: layoutFetched,
          } = res;

          const {
            userIds: userIdsFetched = [],
            groupIds: groupIdsFetched = [],
            type: typeFetched,
            valueType: valueTypeFetched,
            percentageConfig: percentageConfigFetched,
            divisionIds: divisionIdsFetched = [],
            templateIds: templateIdsFetched = [],
          } = queryParamsFetched;

          let usersTypeFetched = USERS_TYPE.ALL;
          if (userIdsFetched && userIdsFetched.length > 0) {
            usersTypeFetched = USERS_TYPE.USERS;
          } else if (groupIdsFetched && groupIdsFetched.length > 0) {
            usersTypeFetched = USERS_TYPE.GROUPS;
          }

          internalFormRef.current.setFieldsValue({
            title: titleFetched || '',
            description: descriptionFetched || '',
            userIds: userIdsFetched || [],
            groups: groupIdsFetched || [],
            type: typeFetched,
            valueType: valueTypeFetched || EXPRESSION_VALUE_TYPE_DEFAULT,
            percentageConfig: percentageConfigFetched || undefined,
            id: chartId,
            layout: layoutFetched || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.COUNTER_CHARTS],
            divisionFilter:
              divisionIdsFetched.length > 0 ? DIVISIONS_TYPE.DIVISIONS : DIVISIONS_TYPE.ALL,
            divisionIds: divisionIdsFetched,
            templateIds: templateIdsFetched,
            usersType: usersTypeFetched,
          });

          setType(typeFetched ?? null);
        }
      } catch (error) {
        console.error('Error loading counter chart:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [boardId, divId, orgId, chartId, counterChartService]);

  useEffect(() => {
    if (fetchedChartData && !loadingSchedules && chartId) {
      const { scheduleIds = [] } = fetchedChartData?.queryParams || {};

      if (scheduleIds && scheduleIds.length > 0) {
        setInitialScheduleIds(scheduleIds);
        internalFormRef.current.setFieldValue('schedulesIds', scheduleIds);
      } else {
        setInitialScheduleIds([]);
        setSelectedSchedules({ byId: {}, selectedAll: true });
      }

      setFetchedChartData(null);
    }
  }, [fetchedChartData, chartId, loadingSchedules]);

  return {
    type,
    selectedSchedules,
    setSelectedSchedules,
    watchUsersType,
    loading,
    loadingSchedules,
    setLoadingSchedules,
    initialScheduleIds,
    fetchedChartData,
    orgId,
    internalFormRef,
    valueType,
    watchDivisionFilter,
    watchDivisionIds,
    divisionsOptions,
    groupOptions,
    groupsLoading,
    handlePopupScrollGroups,
    buildBody,
    resetSelectedSchedules,
    getImperativeHandle,
    resetTemplateIds,
    resetUsersAndGroups,
  };
};

export default useCounterForm;
